/**
 * F 371 — the editor-chrome context.
 *
 * WHY A CONTEXT AND NOT PROPS
 * ---------------------------
 * The floating toolbar is rendered deep inside BlockNote's own tree:
 *
 *   PageEditor → BlockNoteView → FormattingToolbarController → the toolbar
 *
 * `FormattingToolbarController` takes a `formattingToolbar` prop, and F
 * 202's test pins that EXACT form:
 *
 *   <FormattingToolbarController formattingToolbar={CompactSelectionToolbar} />
 *
 * A props-based approach would need the controller to forward a payload,
 * which changes that pinned form. A React context carries the same data
 * without touching the controller's shape — and the toolbar can then be
 * rendered from anywhere, including by a future caller that is not
 * PageEditor at all.
 *
 * WHAT CARRIES WHAT
 * -----------------
 *   pageId       the page being edited — needed to build a block link
 *   moveTargets  the other pages a block can be moved to
 *   onMoveTo     the move itself
 *
 * The context is OPTIONAL. With no provider, the toolbar still renders its
 * formatting controls; only the link and the move are unavailable, and
 * they say so rather than pretending.
 */
import React, { createContext, useContext } from 'react';

export interface EditorChromeContextValue {
  /** The page the edited document belongs to. */
  pageId?: string | null;
  /** Other pages a block can be moved to. */
  moveTargets?: { id: string; title: string }[];
  /** Move the given block(s) into another page. */
  onMoveTo?: (pageId: string, blockIds: string[]) => void;
}

export const EditorChromeContext = createContext<EditorChromeContextValue | undefined>(
  undefined,
);

/**
 * The editor-chrome context, or an empty object when there is no provider.
 *
 * Returning an empty object rather than throwing keeps a toolbar usable
 * in a context that does not care about links or moves — the formatting
 * controls are the important part and they must never fail to render.
 */
export function useEditorChrome(): EditorChromeContextValue {
  return useContext(EditorChromeContext) ?? {};
}

export default EditorChromeContext;
